/**
 * หน้าจอ "ตั้งค่า Kanban" (Replenishment Policy) — P2-7f ครึ่งบน, gate ด้วย
 * `KANBAN_CONFIG` เท่านั้น (D-102 ข้อ 1)
 *
 * ปิดแปดในแปดเส้นทางของ `KANBAN_CONFIG` ที่ `apps/api/.../modules/kanban/router.py`
 * serve จริง: `GET`/`POST /v1/replenishment-policies`, `approve`/`cancel`,
 * `GET`/`POST .../control-cycles` และ `POST .../control-cycles/{cycle_id}/cards`
 *
 * **สี่ข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้ (invariant ของ
 * `WO-P2-M7-006`):**
 *
 * 1. **นี่คือครึ่งเดียวของ D-102 — ครึ่งที่ตัดสินว่าไลน์นี้มีกำลังการผลิตเท่าไร**
 *    (`policy`, `control cycle`, การออกการ์ด) `KanbanSignalScreen` เป็นอีกจอหนึ่ง
 *    ที่แยกกันโดยสิ้นเชิงด้วยโมดูล `KANBAN_EXECUTION` — ไม่มีฟังก์ชันใดในไฟล์นี้
 *    เรียก `lib/kanban/client.ts` ฝั่ง signal เลย
 * 2. **ผู้อนุมัติต้องไม่ใช่ผู้สร้าง (D-010) และหน้าจอไม่เดาล่วงหน้า** — ตารางแสดง
 *    `created_by_employee_id` ทุกแถว ปุ่ม "อนุมัติ" ยังคงแสดงเสมอที่ `DRAFT` และเมื่อ
 *    เซิร์ฟเวอร์ปฏิเสธด้วย `422 APPROVER_MUST_DIFFER_FROM_CREATOR` คำอธิบายนั้นแสดง
 *    ที่แถวนั้นตรง ๆ — ปุ่มที่ถูกซ่อนไม่สอนอะไรเลย ปุ่มที่ถูกปฏิเสธสอนกฎ
 * 3. **หนึ่ง flow มี policy ที่ `ACTIVE` ได้ไม่เกินหนึ่งใบ** (partial unique index) —
 *    หน้าจอแสดงกฎนี้แต่ไม่ซ่อนปุ่ม "เขียน policy ใหม่" เพราะแบบนั้น: ฐานข้อมูลเป็น
 *    ผู้ตัดสิน ไม่ใช่เดาฝั่งเบราว์เซอร์ที่จะผิดทันทีที่มีสองคนทำงานพร้อมกัน
 * 4. **ขอบเขตโรงงานเป็นคำตอบของเซิร์ฟเวอร์** — ไม่กรองซ้ำฝั่งเบราว์เซอร์ และไม่เดา
 *    ว่าโรงงานไหน "ควร" อยู่ในขอบเขต
 *
 * โครงยืมมาจาก `PurchaseRequisitionScreen` — ช่องค้นหาบนสุด ฟอร์มสร้างอยู่หลังปุ่ม
 * ในกล่อง `Modal`, หลังบันทึกสำเร็จโหลดรายการใหม่ผ่าน client เส้นเดิม, ปุ่ม
 * lifecycle รายแถว
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  approveReplenishmentPolicy,
  cancelReplenishmentPolicy,
  createKanbanControlCycle,
  createReplenishmentPolicy,
  currentStatusFromFailure,
  fetchKanbanControlCycles,
  fetchReplenishmentPolicies,
  issueKanbanCard,
  ruleFromFailure,
  type ApiErrorEnvelope,
  type KanbanFailure,
  type PolicyTransition,
  type ReplenishmentPolicyWriteResult,
} from '../../lib/kanban/client';
import {
  canIssueCardOn,
  cycleStatusTone,
  filterReplenishmentPolicies,
  policyActionLabel,
  policyActionTargetStatus,
  policyLifecycleActionsFor,
  policyModeLabel,
  policyStatusLabel,
  policyStatusTone,
  toCreateReplenishmentPolicyBody,
  type CreateReplenishmentPolicyFields,
} from '../../lib/kanban/kanbanView';
import {
  APPROVER_MUST_DIFFER_FROM_CREATOR,
  CARD_CODE_ALREADY_ISSUED,
  CARD_CODE_MAX_LENGTH,
  CARD_FIELD_IS_SERVER_OWNED,
  CYCLE_CARD_COUNT_EXCEEDED,
  CYCLE_IS_NOT_ACTIVE,
  EFFECTIVE_RANGE_IS_NOT_ORDERED,
  FLOW_ALREADY_HAS_AN_ACTIVE_POLICY,
  KANBAN_CONFIG_MODULE,
  LOCATION_IN_ANOTHER_PLANT,
  LOCATION_NOT_FOUND,
  NOTE_MAX_LENGTH,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  POLICY_FIELD_IS_SERVER_OWNED,
  POLICY_IS_NOT_ACTIVE,
  POLICY_STATUS_CONFLICT,
  PRODUCT_CODE_MAX_LENGTH,
  PRODUCT_NOT_ACTIVE,
  PRODUCT_NOT_FOUND,
  PULL_MODE,
  PUSH_MODE,
  SOURCE_AND_DESTINATION_ARE_THE_SAME,
  STORAGE_LOCATION_CODE_MAX_LENGTH,
  WORK_CENTER_CODE_MAX_LENGTH,
  WORK_CENTER_NOT_FOUND,
  WORK_CENTER_IN_ANOTHER_PLANT,
  type KanbanControlCycle,
  type ReplenishmentPolicy,
} from '../../lib/kanban/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type PolicyFailureContext = 'policies' | 'create' | PolicyTransition | 'cycles' | 'create-cycle' | 'issue-card';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function isWriteContext(context: PolicyFailureContext): boolean {
  return context !== 'policies' && context !== 'cycles';
}

function ruleCopy(rule: string | null, currentStatus: string | null): FailureCopy | null {
  switch (rule) {
    case POLICY_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${POLICY_FIELD_IS_SERVER_OWNED} — สถานะ revision ผู้เขียน ผู้อนุมัติ และเวลาเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ — แจ้งผู้ดูแลระบบ`,
      };
    case CARD_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของการ์ดที่เซิร์ฟเวอร์เป็นเจ้าของถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${CARD_FIELD_IS_SERVER_OWNED} — แจ้งผู้ดูแลระบบ`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — ตั้งค่า Kanban ให้โรงงานที่ไม่ได้อยู่ในสิทธิ์เข้าถึงของคุณไม่ได้ (D-004)`,
      };
    case PRODUCT_NOT_FOUND:
      return {
        heading: 'ไม่พบสินค้ารหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_FOUND}`,
      };
    case PRODUCT_NOT_ACTIVE:
      return {
        heading: 'สินค้ารหัสนี้ยังไม่ ACTIVE',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_ACTIVE}`,
      };
    case LOCATION_NOT_FOUND:
      return {
        heading: 'ไม่พบที่เก็บรหัสนี้ในโรงงานที่ระบุ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOCATION_NOT_FOUND}`,
      };
    case LOCATION_IN_ANOTHER_PLANT:
      return {
        heading: 'ที่เก็บที่ระบุเป็นของโรงงานอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOCATION_IN_ANOTHER_PLANT} — รหัสที่เก็บไม่ซ้ำกันข้ามโรงงาน (unique ต่อโรงงานเท่านั้น)`,
      };
    case WORK_CENTER_NOT_FOUND:
      return {
        heading: 'ไม่พบศูนย์งานรหัสนี้ในโรงงานที่ระบุ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${WORK_CENTER_NOT_FOUND}`,
      };
    case WORK_CENTER_IN_ANOTHER_PLANT:
      return {
        heading: 'ศูนย์งานที่ระบุเป็นของโรงงานอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${WORK_CENTER_IN_ANOTHER_PLANT}`,
      };
    case SOURCE_AND_DESTINATION_ARE_THE_SAME:
      return {
        heading: 'ต้นทางและปลายทางเป็นที่เก็บเดียวกัน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SOURCE_AND_DESTINATION_ARE_THE_SAME} — flow ต้องเคลื่อนของจากที่หนึ่งไปอีกที่หนึ่งจริง`,
      };
    case EFFECTIVE_RANGE_IS_NOT_ORDERED:
      return {
        heading: 'ช่วงวันที่มีผลไม่ถูกต้อง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${EFFECTIVE_RANGE_IS_NOT_ORDERED} — วันสิ้นสุดต้องอยู่หลังวันเริ่มต้น`,
      };
    case FLOW_ALREADY_HAS_AN_ACTIVE_POLICY:
      return {
        heading: 'flow นี้มี policy ที่ใช้งานอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${FLOW_ALREADY_HAS_AN_ACTIVE_POLICY} — หนึ่ง flow (สินค้า+โรงงาน+ต้นทาง+ปลายทาง) มี policy ที่ ACTIVE ได้ไม่เกินหนึ่งใบพร้อมกัน (D-102) โหลดรายการใหม่เพื่อดูใบที่ใช้งานอยู่`,
      };
    case POLICY_STATUS_CONFLICT:
      return {
        heading: 'สถานะของ policy นี้ไม่ตรงกับที่หน้าจอเห็น',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${POLICY_STATUS_CONFLICT}${
          currentStatus === null ? '' : ` ขณะนี้ใบนี้อยู่ที่ ${policyStatusLabel(currentStatus)}`
        } โหลดรายการใหม่ก่อนลองอีกครั้ง — **การกดซ้ำจะได้คำตอบเดิม**`,
      };
    case APPROVER_MUST_DIFFER_FROM_CREATOR:
      return {
        heading: 'ผู้อนุมัติต้องไม่ใช่ผู้เขียน policy นี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_CREATOR} — **ไม่ใช่เรื่องสิทธิ์ไม่พอ** คุณถือ ${KANBAN_CONFIG_MODULE} + APPROVE ครบแล้ว แต่ใบนี้คุณเป็นผู้เขียนเอง การแบ่งแยกหน้าที่ (D-010) จึงให้คนอื่นเป็นผู้อนุมัติ **ใบนี้ยังอยู่ที่สถานะเดิม**`,
      };
    case POLICY_IS_NOT_ACTIVE:
      return {
        heading: 'ตัด control cycle บน policy ที่ยังไม่ ACTIVE ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${POLICY_IS_NOT_ACTIVE} — ต้องอนุมัติ policy ให้เป็น ACTIVE ก่อนจึงจะตัด control cycle ได้`,
      };
    case CYCLE_IS_NOT_ACTIVE:
      return {
        heading: 'ออกการ์ดบน control cycle ที่ไม่ใช่ ACTIVE ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${CYCLE_IS_NOT_ACTIVE}`,
      };
    case CYCLE_CARD_COUNT_EXCEEDED:
      return {
        heading: 'ออกการ์ดเกินจำนวนที่ cycle นี้ประกาศไว้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${CYCLE_CARD_COUNT_EXCEEDED} — ตัด control cycle ใหม่ที่มีจำนวนมากขึ้นก่อน (การตัดใหม่แทนที่ cycle เดิมโดยอัตโนมัติ)`,
      };
    case CARD_CODE_ALREADY_ISSUED:
      return {
        heading: 'รหัสการ์ดนี้ถูกใช้ไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${CARD_CODE_ALREADY_ISSUED} — รหัสการ์ดไม่ซ้ำกันทั้งระบบ (ไม่ใช่แค่ในโรงงานเดียว)`,
      };
    default:
      return null;
  }
}

function failureCopy(failure: KanbanFailure, context: PolicyFailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const currentStatus = currentStatusFromFailure(failure);
  const nothingChanged = writing ? ' ไม่มีอะไรถูกเขียนและไม่มีแถว audit ถูกเพิ่ม' : '';

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${KANBAN_CONFIG_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบข้อมูลที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ไม่มีอยู่แล้ว หรืออยู่นอกขอบเขตของคุณ โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — สิ่งที่ส่งไปชนกับสถานะที่เก็บอยู่จริง ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

function envelopeOf(failure: KanbanFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

export interface PolicyFailureNoticeProps {
  readonly failure: KanbanFailure;
  readonly context: PolicyFailureContext;
  readonly onRetry?: () => void;
}

export function PolicyFailureNotice({ failure, context, onRetry }: PolicyFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="policy-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="policy-rule">
            {rule}
          </code>
        </p>
      )}
      {serverMessage === null ? null : (
        <p className="cap-notice__note">ข้อความจากเซิร์ฟเวอร์: {serverMessage}</p>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface ReplenishmentPolicyScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type PoliciesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly ReplenishmentPolicy[] }
  | KanbanFailure;

type CyclesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly KanbanControlCycle[] }
  | KanbanFailure;

type Pending =
  | { readonly kind: 'create' }
  | { readonly kind: PolicyTransition; readonly policyId: string }
  | { readonly kind: 'create-cycle'; readonly policyId: string }
  | { readonly kind: 'issue-card'; readonly cycleId: string };

interface ActionOutcome {
  readonly policyId: string;
  readonly transition: PolicyTransition;
  readonly failure: KanbanFailure | null;
  readonly policy: ReplenishmentPolicy | null;
}

const EMPTY_CREATE_FIELDS: CreateReplenishmentPolicyFields = {
  plantCode: '',
  productCode: '',
  sourceLocationCode: '',
  destinationLocationCode: '',
  upstreamWorkCenterCode: '',
  downstreamWorkCenterCode: '',
  mode: PULL_MODE,
  containerQty: '',
  cardCount: '',
  effectiveFrom: '',
  effectiveTo: '',
  note: '',
};

export function ReplenishmentPolicyScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ReplenishmentPolicyScreenProps) {
  const canCreate = can(permissions, KANBAN_CONFIG_MODULE, 'CREATE');
  const canEdit = can(permissions, KANBAN_CONFIG_MODULE, 'EDIT');
  const canApprove = can(permissions, KANBAN_CONFIG_MODULE, 'APPROVE');
  const canWriteAnything = canCreate || canEdit || canApprove;

  const [policies, setPolicies] = useState<PoliciesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);
  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] =
    useState<CreateReplenishmentPolicyFields>(EMPTY_CREATE_FIELDS);
  const [createFailure, setCreateFailure] = useState<KanbanFailure | null>(null);
  const [createdPolicy, setCreatedPolicy] = useState<ReplenishmentPolicy | null>(null);

  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);

  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [cycles, setCycles] = useState<CyclesState>({ status: 'loading' });
  const [cyclesReloadToken, setCyclesReloadToken] = useState(0);
  const [cycleCardCount, setCycleCardCount] = useState('');
  const [cycleFailure, setCycleFailure] = useState<KanbanFailure | null>(null);
  const [cardCodeByCycle, setCardCodeByCycle] = useState<Record<string, string>>({});
  const [cardFailure, setCardFailure] = useState<KanbanFailure | null>(null);
  const [issuedCardCode, setIssuedCardCode] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setPolicies((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    void fetchReplenishmentPolicies({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setPolicies(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  useEffect(() => {
    if (selectedPolicyId === null) {
      return;
    }
    const controller = new AbortController();
    let active = true;
    setCycles({ status: 'loading' });

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    void fetchKanbanControlCycles(selectedPolicyId, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setCycles(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, selectedPolicyId, cyclesReloadToken]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    setCreateFields(EMPTY_CREATE_FIELDS);
    setCreateFailure(null);
    setCreatedPolicy(null);
    setCreateOpen(true);
  };

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateField = (field: keyof CreateReplenishmentPolicyFields, value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  const submitCreate = async () => {
    const body = toCreateReplenishmentPolicyBody(createFields);
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);
    setCreatedPolicy(null);

    const result = await createReplenishmentPolicy(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCreateFailure(result);
      return;
    }

    setCreatedPolicy(result.policy);
    setCreateOpen(false);
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  const runTransition = async (policy: ReplenishmentPolicy, transition: PolicyTransition) => {
    const required = transition === 'approve' ? 'APPROVE' : 'EDIT';
    if (pending !== null || !can(permissions, KANBAN_CONFIG_MODULE, required)) {
      return;
    }

    setPending({ kind: transition, policyId: policy.policy_id });
    setActionOutcome(null);

    const body = { expected_status: policy.status };
    const options = { apiBaseUrl, fetchImpl };

    let result: ReplenishmentPolicyWriteResult;
    if (transition === 'approve') {
      result = await approveReplenishmentPolicy(policy.policy_id, body, options);
    } else {
      result = await cancelReplenishmentPolicy(policy.policy_id, body, options);
    }

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setActionOutcome({
        policyId: policy.policy_id,
        transition,
        failure: result,
        policy: null,
      });
      return;
    }

    setActionOutcome({
      policyId: policy.policy_id,
      transition,
      failure: null,
      policy: result.policy,
    });
    setReloadToken((token) => token + 1);
    if (transition === 'approve' && selectedPolicyId === policy.policy_id) {
      setCyclesReloadToken((token) => token + 1);
    }
  };

  const selectPolicy = (policyId: string) => {
    setSelectedPolicyId((current) => (current === policyId ? null : policyId));
    setCycleCardCount('');
    setCycleFailure(null);
    setCardFailure(null);
    setIssuedCardCode(null);
  };

  const submitCreateCycle = async (policy: ReplenishmentPolicy) => {
    if (pending !== null || !canCreate || cycleCardCount.trim() === '') {
      return;
    }
    const count = Number(cycleCardCount.trim());
    if (!Number.isInteger(count) || count <= 0) {
      return;
    }

    setPending({ kind: 'create-cycle', policyId: policy.policy_id });
    setCycleFailure(null);

    const result = await createKanbanControlCycle(
      policy.policy_id,
      { card_count: count },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCycleFailure(result);
      return;
    }
    setCycleCardCount('');
    setCyclesReloadToken((token) => token + 1);
  };

  const submitIssueCard = async (policy: ReplenishmentPolicy, cycle: KanbanControlCycle) => {
    const cardCode = (cardCodeByCycle[cycle.cycle_id] ?? '').trim();
    if (pending !== null || !canCreate || cardCode === '') {
      return;
    }

    setPending({ kind: 'issue-card', cycleId: cycle.cycle_id });
    setCardFailure(null);
    setIssuedCardCode(null);

    const result = await issueKanbanCard(
      policy.policy_id,
      cycle.cycle_id,
      { card_code: cardCode },
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCardFailure(result);
      return;
    }
    setIssuedCardCode(result.card.card_code);
    setCardCodeByCycle((previous) => ({ ...previous, [cycle.cycle_id]: '' }));
  };

  const items = policies.status === 'ready' ? policies.items : [];
  const matchedItems = filterReplenishmentPolicies(items, query);
  const busy = pending !== null;
  const createBody = toCreateReplenishmentPolicyBody(createFields);
  const selectedPolicy = items.find((policy) => policy.policy_id === selectedPolicyId) ?? null;

  const offeredActions = (policy: ReplenishmentPolicy): readonly PolicyTransition[] =>
    policyLifecycleActionsFor(policy.status).filter((transition) =>
      can(permissions, KANBAN_CONFIG_MODULE, transition === 'approve' ? 'APPROVE' : 'EDIT'),
    );

  const anyRowHasActions = matchedItems.some((policy) => offeredActions(policy).length > 0);

  return (
    <div className="cap-page" data-testid="replenishment-policies">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ตั้งค่า Kanban (Replenishment Policy)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  กติกาการเติมของของแต่ละ flow — <strong>ตั้ง policy, ตัด control cycle
                  และออกการ์ดที่นี่เท่านั้น</strong> เพราะนี่คือการตัดสินกำลังการผลิตของไลน์
                  ซึ่งเป็นสิ่งที่ operator หน้าไลน์ต้องแตะไม่ได้ (D-102 ข้อ 1) — การสแกนเบิก/
                  รับทราบ/เติมของจริงอยู่ที่หน้าจอ &quot;บอร์ด Kanban&quot; ซึ่งเป็นคนละสิทธิ์กัน
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="policy-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเขียน policy ไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>
            , <code className="cap-table__code">can_edit</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{KANBAN_CONFIG_MODULE}</code> — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
        </div>
      ) : null}

      <div className="cap-filters" data-testid="policy-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหา policy</span>
          <input
            className="cap-input"
            type="search"
            data-testid="policy-search"
            value={query}
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหา"
            text="กรองจากรหัสสินค้า โรงงาน ต้นทาง หรือปลายทาง — กรองจากรายการที่โหลดมาแล้ว"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="policy-create-open"
            onClick={openCreate}
          >
            เขียน policy ใหม่
          </button>
        ) : null}
      </div>

      <Modal open={canCreate && createOpen} onClose={closeCreate} title="เขียน policy ใหม่">
        <form
          className="cap-form"
          data-testid="policy-create-form"
          aria-label="เขียน policy ใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              data-testid="policy-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('plantCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า</span>
            <input
              className="cap-input"
              data-testid="policy-create-product"
              value={createFields.productCode}
              maxLength={PRODUCT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('productCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บต้นทาง</span>
            <input
              className="cap-input"
              data-testid="policy-create-source"
              value={createFields.sourceLocationCode}
              maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('sourceLocationCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บปลายทาง</span>
            <input
              className="cap-input"
              data-testid="policy-create-destination"
              value={createFields.destinationLocationCode}
              maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('destinationLocationCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ศูนย์งานต้นทาง (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="policy-create-upstream"
              value={createFields.upstreamWorkCenterCode}
              maxLength={WORK_CENTER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('upstreamWorkCenterCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ศูนย์งานปลายทาง (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="policy-create-downstream"
              value={createFields.downstreamWorkCenterCode}
              maxLength={WORK_CENTER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('downstreamWorkCenterCode', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">โหมด</span>
            <select
              className="cap-input"
              data-testid="policy-create-mode"
              value={createFields.mode}
              onChange={(event) => updateField('mode', event.target.value)}
            >
              <option value={PULL_MODE}>{policyModeLabel(PULL_MODE)}</option>
              <option value={PUSH_MODE}>{policyModeLabel(PUSH_MODE)}</option>
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ปริมาณต่อภาชนะ (container_qty)</span>
            <input
              className="cap-input"
              data-testid="policy-create-container-qty"
              value={createFields.containerQty}
              inputMode="decimal"
              autoComplete="off"
              onChange={(event) => updateField('containerQty', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">จำนวนการ์ด (card_count)</span>
            <input
              className="cap-input"
              data-testid="policy-create-card-count"
              value={createFields.cardCount}
              inputMode="numeric"
              autoComplete="off"
              onChange={(event) => updateField('cardCount', event.target.value)}
            />
            <InfoHint
              label="เกี่ยวกับจำนวนการ์ด"
              text="จำนวนการ์ดที่ตั้งใจให้หมุนเวียน — ไม่มี min/max แยก ใช้จำนวนคงที่ (D-102)"
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">มีผลตั้งแต่วันที่</span>
            <input
              className="cap-input"
              type="date"
              data-testid="policy-create-effective-from"
              value={createFields.effectiveFrom}
              onChange={(event) => updateField('effectiveFrom', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">มีผลถึงวันที่ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              type="date"
              data-testid="policy-create-effective-to"
              value={createFields.effectiveTo}
              onChange={(event) => updateField('effectiveTo', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="policy-create-note"
              value={createFields.note}
              maxLength={NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => updateField('note', event.target.value)}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="policy-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังบันทึก…' : 'บันทึกเป็นร่าง'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="policy-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {createFailure === null ? null : (
            <PolicyFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdPolicy === null ? null : (
        <div className="cap-notice" data-testid="policy-create-result" role="status">
          <StatusBadge tone="success">บันทึกร่างแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            policy ของ {createdPolicy.product_code} ({createdPolicy.plant_code}) เป็นร่างแล้ว
          </h2>
          <p className="cap-notice__text">
            ผู้เขียนคือ {createdPolicy.created_by_employee_id} — ยังต้องมีคนอื่นกดอนุมัติ
            (D-010) ก่อนจึงจะ ACTIVE
          </p>
        </div>
      )}

      {actionOutcome?.policy == null ? null : (
        <div className="cap-notice" data-testid="policy-action-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            policy ของ {actionOutcome.policy.product_code} อยู่ที่สถานะ{' '}
            {policyStatusLabel(actionOutcome.policy.status)} แล้ว
          </h2>
        </div>
      )}

      {policies.status === 'loading' ? (
        <div className="cap-notice" data-testid="policy-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการ policy…</p>
        </div>
      ) : null}

      {policies.status !== 'loading' && policies.status !== 'ready' ? (
        <PolicyFailureNotice
          failure={policies}
          context="policies"
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      ) : null}

      {policies.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="policy-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มี policy</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มี policy ในโรงงานที่คุณเข้าถึงได้</h2>
        </div>
      ) : null}

      {policies.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="policy-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบ policy ที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มี policy ที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
        </div>
      ) : null}

      {policies.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="Replenishment policy ทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">Replenishment Policy ทั้งหมด</h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="policy-table">
              <caption className="cap-visually-hidden">
                policy ของโรงงานที่คุณเข้าถึงได้ พร้อมผู้เขียนและสถานะ
              </caption>
              <thead>
                <tr>
                  <th scope="col">สินค้า</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ต้นทาง → ปลายทาง</th>
                  <th scope="col">โหมด</th>
                  <th scope="col">ภาชนะ × การ์ด</th>
                  <th scope="col">ผู้เขียน</th>
                  <th scope="col">สถานะ</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                  <th scope="col">Control cycle</th>
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((policy) => {
                  const actions = offeredActions(policy);
                  const outcome =
                    actionOutcome?.policyId === policy.policy_id && actionOutcome.failure !== null
                      ? actionOutcome
                      : null;
                  const selected = selectedPolicyId === policy.policy_id;

                  return (
                    <tr
                      key={policy.policy_id}
                      data-testid={`policy-row-${policy.policy_id}`}
                      data-status={policy.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{policy.product_code}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{policy.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{policy.source_location_code}</code>
                        {' → '}
                        <code className="cap-table__code">{policy.destination_location_code}</code>
                      </td>
                      <td>{policyModeLabel(policy.mode)}</td>
                      <td>
                        {policy.container_qty} × {policy.card_count}
                      </td>
                      <td>
                        {/* D-010: ต้องเห็นผู้เขียนเพื่อให้ผู้อนุมัติรู้ว่าใบนี้เป็นของตัวเองหรือไม่ */}
                        <code className="cap-table__code" data-testid={`policy-created-by-${policy.policy_id}`}>
                          {policy.created_by_employee_id}
                        </code>
                      </td>
                      <td>
                        <StatusBadge tone={policyStatusTone(policy.status)}>
                          {policyStatusLabel(policy.status)}
                        </StatusBadge>
                      </td>
                      {anyRowHasActions ? (
                        <td>
                          {actions.map((transition) => (
                            <button
                              key={transition}
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`policy-action-${transition}-${policy.policy_id}`}
                              disabled={busy}
                              onClick={() => void runTransition(policy, transition)}
                            >
                              {pending?.kind === transition &&
                              'policyId' in pending &&
                              pending.policyId === policy.policy_id
                                ? 'กำลังส่ง…'
                                : policyActionLabel(transition)}
                            </button>
                          ))}
                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="policy-action-failure-slot">
                              <PolicyFailureNotice
                                failure={outcome.failure}
                                context={outcome.transition}
                              />
                              <p className="cap-notice__note">
                                ปุ่มที่กดคือ &quot;{policyActionLabel(outcome.transition)}&quot;
                                ซึ่งพา policy ไปที่{' '}
                                {policyStatusLabel(policyActionTargetStatus(outcome.transition))}
                              </p>
                            </div>
                          )}
                        </td>
                      ) : null}
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`policy-manage-${policy.policy_id}`}
                          aria-expanded={selected}
                          onClick={() => selectPolicy(policy.policy_id)}
                        >
                          {selected ? 'ซ่อน' : 'จัดการ'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {selectedPolicy === null ? null : (
        <section
          className="cap-card"
          aria-label="Control cycle และการ์ด"
          data-testid={`policy-detail-${selectedPolicy.policy_id}`}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              Control cycle ของ {selectedPolicy.product_code} ({selectedPolicy.plant_code})
              <InfoHint
                label="เกี่ยวกับ control cycle"
                text="ตัด control cycle และออกการ์ดได้เฉพาะเมื่อ policy นี้ ACTIVE เท่านั้น
              (409 POLICY_IS_NOT_ACTIVE)"
              />
            </h2>
          </div>

          {cycles.status === 'loading' ? <p className="cap-notice__text">กำลังโหลด…</p> : null}
          {cycles.status !== 'loading' && cycles.status !== 'ready' ? (
            <PolicyFailureNotice
              failure={cycles}
              context="cycles"
              onRetry={() => setCyclesReloadToken((token) => token + 1)}
            />
          ) : null}

          {cycles.status === 'ready' ? (
            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="policy-cycles-table">
                <thead>
                  <tr>
                    <th scope="col">จำนวนการ์ด</th>
                    <th scope="col">สถานะ</th>
                    <th scope="col">ออกการ์ด</th>
                  </tr>
                </thead>
                <tbody>
                  {cycles.status === 'ready' && cycles.items.length === 0 ? (
                    <tr>
                      <td colSpan={3} data-testid="policy-cycles-empty">
                        ยังไม่มี control cycle
                      </td>
                    </tr>
                  ) : null}
                  {cycles.status === 'ready'
                    ? cycles.items.map((cycle) => (
                        <tr key={cycle.cycle_id} data-testid={`policy-cycle-row-${cycle.cycle_id}`}>
                          <td>{cycle.card_count}</td>
                          <td>
                            <StatusBadge tone={cycleStatusTone(cycle.status)}>
                              {cycle.status}
                            </StatusBadge>
                          </td>
                          <td>
                            {canCreate && canIssueCardOn(cycle.status) ? (
                              <form
                                className="cap-form cap-form--inline"
                                data-testid={`policy-card-issue-form-${cycle.cycle_id}`}
                                onSubmit={(event) => {
                                  event.preventDefault();
                                  void submitIssueCard(selectedPolicy, cycle);
                                }}
                              >
                                <input
                                  className="cap-input"
                                  data-testid={`policy-card-code-${cycle.cycle_id}`}
                                  placeholder="รหัสการ์ด"
                                  maxLength={CARD_CODE_MAX_LENGTH}
                                  value={cardCodeByCycle[cycle.cycle_id] ?? ''}
                                  onChange={(event) =>
                                    setCardCodeByCycle((previous) => ({
                                      ...previous,
                                      [cycle.cycle_id]: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="submit"
                                  className="cap-button cap-button--ghost"
                                  data-testid={`policy-card-issue-submit-${cycle.cycle_id}`}
                                  disabled={busy || (cardCodeByCycle[cycle.cycle_id] ?? '').trim() === ''}
                                >
                                  ออกการ์ด
                                </button>
                              </form>
                            ) : (
                              <span className="cap-table__muted">
                                {canIssueCardOn(cycle.status)
                                  ? 'ไม่มีสิทธิ์ออกการ์ด'
                                  : 'cycle นี้ไม่ ACTIVE'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {cardFailure === null ? null : (
            <PolicyFailureNotice failure={cardFailure} context="issue-card" />
          )}
          {issuedCardCode === null ? null : (
            <div className="cap-notice" data-testid="policy-card-issue-result" role="status">
              <StatusBadge tone="success">ออกการ์ดแล้ว</StatusBadge>
              <h3 className="cap-notice__title">ออกการ์ด {issuedCardCode} แล้ว</h3>
              <p className="cap-notice__text">สถานะเริ่มต้นคือ AVAILABLE — พร้อมใช้งานบนบอร์ด Kanban</p>
            </div>
          )}

          {canCreate && cycles.status === 'ready' ? (
            <form
              className="cap-form cap-form--inline"
              data-testid="policy-cycle-create-form"
              aria-label="ตัด control cycle ใหม่"
              onSubmit={(event) => {
                event.preventDefault();
                void submitCreateCycle(selectedPolicy);
              }}
            >
              <label className="cap-field">
                <span className="cap-field__label">จำนวนการ์ดที่หมุนเวียนจริง</span>
                <input
                  className="cap-input"
                  data-testid="policy-cycle-card-count"
                  value={cycleCardCount}
                  inputMode="numeric"
                  onChange={(event) => setCycleCardCount(event.target.value)}
                />
              </label>
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="policy-cycle-create-submit"
                disabled={busy || cycleCardCount.trim() === ''}
              >
                ตัด control cycle ใหม่
              </button>
            </form>
          ) : null}
          {cycleFailure === null ? null : (
            <PolicyFailureNotice failure={cycleFailure} context="create-cycle" />
          )}
        </section>
      )}
    </div>
  );
}
